import React, { useEffect, useRef, useState } from "react";
import { Maximize2, Compass, Image as ImageIcon } from "lucide-react";

// Loads pannellum viewer if the image is 360; else shows regular image
export default function Panorama360Viewer({ url, title, is360 = true, height = "360px" }) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!is360) return;
        let cancelled = false;
        const tryInit = () => {
            if (cancelled) return;
            if (window.pannellum && containerRef.current) {
                if (viewerRef.current) {
                    try { viewerRef.current.destroy(); } catch (_e) { /* noop */ }
                    viewerRef.current = null;
                }
                viewerRef.current = window.pannellum.viewer(containerRef.current, {
                    type: "equirectangular",
                    panorama: url,
                    autoLoad: true,
                    showControls: true,
                    hotSpotDebug: false,
                    compass: true,
                    title: title || "",
                });
                setReady(true);
            } else {
                setTimeout(tryInit, 300);
            }
        };
        tryInit();
        return () => {
            cancelled = true;
            if (viewerRef.current) {
                try { viewerRef.current.destroy(); } catch (_e) { /* noop */ }
                viewerRef.current = null;
            }
        };
    }, [url, title, is360]);

    if (!url) return null;

    const absUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

    if (!is360) {
        return (
            <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-100" style={{ height }}>
                <img src={absUrl} alt={title} className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 bg-white/90 backdrop-blur text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider text-stone-700">
                    <ImageIcon size={10} className="inline mr-1" /> Foto
                </span>
            </div>
        );
    }

    return (
        <div className="relative rounded-xl overflow-hidden border-2 border-emerald-300 bg-black" style={{ height }} data-testid="pano-viewer">
            <div ref={containerRef} style={{ width: "100%", height: "100%" }} data-panorama-url={absUrl.replace(process.env.REACT_APP_BACKEND_URL || "", "")}>
                <div ref={(el) => { if (containerRef.current && el === containerRef.current) return; }} />
            </div>
            <span className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider z-10">
                <Compass size={10} className="inline mr-1" /> 360°
            </span>
            {!ready && (
                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs">
                    Caricamento viewer 360°...
                </div>
            )}
        </div>
    );
}

// Direct init helper — call from useEffect to init pannellum on a container
export const initPano = (container, url) => {
    if (window.pannellum && container) {
        return window.pannellum.viewer(container, {
            type: "equirectangular", panorama: url, autoLoad: true, showControls: true, compass: true,
        });
    }
    return null;
};
